
import { useState } from 'react';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', delay = 0, className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation, delay)} ${className}`}>
      {children}
    </div>
  );
}

export default function PricingHero() {
  const [annual, setAnnual] = useState(true);

  const plans = [
    {
      name: 'Básico',
      description: 'Perfecto para consultorios independientes que inician su transformación digital.',
      priceMonthly: 49,
      priceAnnual: 39,
      cta: 'Comenzar Ahora',
      popular: false,
      features: [
        'Hasta 2 usuarios',
        'Gestión de pacientes básica',
        'Agenda digital inteligente',
        'Historiales clínicos digitales',
        'Reportes básicos mensuales',
        'Soporte por email',
        'Almacenamiento 5 GB',
      ],
    },
    {
      name: 'Profesional',
      description: 'Ideal para clínicas en crecimiento que necesitan herramientas avanzadas.',
      priceMonthly: 129,
      priceAnnual: 99,
      cta: 'Registrarse / Ingresar',
      popular: true,
      features: [
        'Hasta 10 usuarios',
        'Módulo de Óptica integrado',
        'Gestión de inventario completa',
        'Diagnóstico asistido avanzado',
        'Reportes y analíticas avanzadas',
        'Integración con laboratorios',
        'Soporte prioritario 24/7',
        'Almacenamiento 50 GB',
        'Recordatorios automáticos SMS',
      ],
    },
    {
      name: 'Enterprise',
      description: 'Para grandes clínicas y cadenas que requieren personalización total.',
      priceMonthly: 0,
      priceAnnual: 0,
      cta: 'Contactar Ventas',
      popular: false,
      features: [
        'Usuarios ilimitados',
        'Multi-consultorio completo',
        'API personalizada',
        'Capacitación dedicada presencial',
        'Gerente de cuenta exclusivo',
        'SLA 99.99% garantizado',
        'Migración asistida de datos',
        'Almacenamiento ilimitado',
        'Integraciones a medida',
        'Facturación personalizada',
      ],
    },
  ];

  return (
    <section className="pt-32 pb-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-50/50 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Planes y Precios</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Planes diseñados para el<br /><span className="font-display italic text-emerald-600">futuro de tu óptica</span>
            </h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
              Desde consultorios independientes hasta cadenas de clínicas, tenemos la solución perfecta para ti. Sin contratos a largo plazo.
            </p>

            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${!annual ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${annual ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <span>Anual</span>
                <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-bold whitespace-nowrap">Ahorra 20%</span>
              </button>
            </div>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <AnimatedSection key={plan.name} animation="slide-up" delay={index * 150}>
              <div className={`relative bg-white rounded-2xl p-8 h-full flex flex-col transition-all hover:-translate-y-2 hover:shadow-2xl cursor-pointer ${plan.popular
                ? 'border-2 border-emerald-500 shadow-xl shadow-emerald-500/10'
                : 'border border-gray-100 shadow-md'
                }`}>
                {plan.popular && (
                  <div className="absolute -top-3 right-6 bg-emerald-500 text-white text-[10px] font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                    Más Popular
                  </div>
                )}

                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-500 mb-1">{plan.name}</p>
                  <div className="flex items-baseline space-x-1 mb-3">
                    {plan.priceMonthly === 0 ? (
                      <span className="text-4xl font-bold text-gray-900">Personalizado</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-gray-900">${annual ? plan.priceAnnual : plan.priceMonthly}</span>
                        <span className="text-sm text-gray-500">/mes</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{plan.description}</p>
                </div>

                <a
                  href={plan.priceMonthly === 0 ? '/#contacto' : 'https://anamnesysoft.com/'}
                  target={plan.priceMonthly !== 0 ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className={`block text-center py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer mb-7 ${plan.popular
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-500/20'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                >
                  {plan.cta}
                </a>

                <div className="space-y-3 flex-1">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-600 text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
